import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../services/api";

/**
 * Resale feature settings: the company WhatsApp number shown on every resale
 * unit in the app and the commission percentages (1.25% buyer / 0% seller).
 * These live in their own table, so nothing here touches the general settings.
 */
const ResaleSettingsCard = () => {
  const [settings, setSettings] = useState({
    whatsapp_number: "",
    buyer_commission_percent: "",
    seller_commission_percent: "",
    notification_email: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/resaleSettings")
      .then((res) => {
        if (res.data.status) {
          const response = res.data.response || {};
          setSettings({
            whatsapp_number: response.whatsapp_number ?? "",
            buyer_commission_percent: response.buyer_commission_percent ?? "",
            seller_commission_percent: response.seller_commission_percent ?? "",
            notification_email: response.notification_email ?? "",
          });
        } else {
          toast.error("Failed to load the resale settings");
        }
      })
      .catch((error) => {
        console.error("Error fetching resale settings:", error);
        toast.error("Failed to fetch the resale settings");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    setSaving(true);
    api
      .put("/resaleSettings", {
        whatsapp_number: settings.whatsapp_number,
        buyer_commission_percent: settings.buyer_commission_percent === "" ? null : settings.buyer_commission_percent,
        seller_commission_percent:
          settings.seller_commission_percent === "" ? null : settings.seller_commission_percent,
        notification_email: settings.notification_email || null,
      })
      .then((res) => {
        if (res.data.status) {
          toast.success("Resale settings updated successfully");
        } else {
          toast.error("Failed to update the resale settings");
        }
      })
      .catch((error) => {
        console.error("Error updating resale settings:", error);
        toast.error(error?.response?.data?.message || "Error updating the resale settings");
      })
      .finally(() => setSaving(false));
  };

  if (loading) return <p>Loading resale settings...</p>;

  return (
    <div className="card radius-12">
      <div className="card-body p-24">
        <h6 className="mb-16">Resale Units Settings</h6>
        <p className="text-sm text-secondary-light mb-20">
          The WhatsApp number below is the one shown on every resale unit in the app. The commission percentages are
          applied to the contract value of every resale unit and are used everywhere: the app, the dashboard and the
          emails. Changing them here updates all listings immediately (set a side to 0 to charge it nothing).
        </p>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="row">
            <div className="col-sm-6">
              <div className="mb-20">
                <label
                  htmlFor="whatsapp_number"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Company WhatsApp Number
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="whatsapp_number"
                  name="whatsapp_number"
                  placeholder="201000000000"
                  value={settings.whatsapp_number}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="mb-20">
                <label
                  htmlFor="notification_email"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Notifications Email
                </label>
                <input
                  type="email"
                  className="form-control radius-8"
                  id="notification_email"
                  name="notification_email"
                  placeholder="backend@thebrokerz.com"
                  value={settings.notification_email}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="mb-20">
                <label
                  htmlFor="buyer_commission_percent"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Buyer Commission ( % )
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control radius-8"
                  id="buyer_commission_percent"
                  name="buyer_commission_percent"
                  value={settings.buyer_commission_percent}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="mb-20">
                <label
                  htmlFor="seller_commission_percent"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Seller Commission ( % )
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control radius-8"
                  id="seller_commission_percent"
                  name="seller_commission_percent"
                  value={settings.seller_commission_percent}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary radius-8"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Spinner animation="border" size="sm" /> : "Update Resale Settings"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResaleSettingsCard;
